/**
 * Plan feature bullets.
 *
 * These are marketing copy, not entitlements — the backend is the source of truth for what a plan
 * actually allows. `/rest/v1/paddle/products` returns price ids and amounts only, so the bullets
 * live here rather than being hardcoded inside the fetch (as they were on the marketing site).
 */

export const PRO_FEATURES = [
  '25 hours of transcription per month',
  'Files up to 4 hours long',
  'Priority support',
];

export const PRO_MONTHLY_NAME = 'Pro Plan';
export const PRO_YEARLY_NAME = 'Pro Plan (Yearly)';

/**
 * What yearly billing saves vs paying monthly for 12 months, rounded to the cent. Computed from
 * the live Paddle prices rather than a hardcoded "20%" — the two must never be able to disagree.
 */
export function yearlySavings(monthlyPrice: number, yearlyPrice: number): number {
  return Math.round((monthlyPrice * 12 - yearlyPrice) * 100) / 100;
}

/**
 * The trial signup checkout runs. This lives on the Paddle price itself — `/paddle/products`
 * returns nothing about it (docs/BACKEND_REQUESTS.md §2-3) — so these are plain hardcoded copy, the
 * single place to edit if the configured trial ever changes. Confirmed live in both Paddle sandbox
 * and production as of 2026-09.
 */
export const TRIAL_PRICE_USD = 5;
export const TRIAL_DAYS = 7;

/**
 * The one thing about a plan the server's `GET /rest/v1/usage` doesn't cover: the byte ceiling on a
 * single upload. Transcription seconds (used, remaining, per-file cap) are the server's numbers —
 * see `usageThunks.ts` — not derived here.
 */
export interface PlanAllowance {
  /** Largest single upload, in bytes. */
  maxFileBytes: number;
}

/**
 * Not a tier anyone can sign up into — signup requires a card, and a trialing subscription reads as
 * Pro. This is what a *lapsed* account falls back to: cancelled, expired, or payment failed.
 */
export const LAPSED_ALLOWANCE: PlanAllowance = {
  maxFileBytes: 50 * 1024 * 1024,
};

export const PRO_ALLOWANCE: PlanAllowance = {
  // AssemblyAI's ceiling, which is the real limit for a paid user.
  maxFileBytes: 5 * 1024 * 1024 * 1024,
};

export function allowanceForPlan(isPro: boolean): PlanAllowance {
  return isPro ? PRO_ALLOWANCE : LAPSED_ALLOWANCE;
}
